"use client";

import { JSX, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
} from "@/components/ui/sheet";
import {
  Eye,
  Home,
  School,
  FileUser,
  GraduationCap,
  Newspaper,
  DoorOpen,
  MapPinHouse,
  User,
  ChartLine,
  FilePlus,
  Images,
} from "lucide-react";
import { Sling as Hamburger } from "hamburger-react";
import { MobileNavItem } from "./mobile-nav-item";
import { usePagesVisibility } from "@/hooks/use-pages-visibility";
import { isHrefVisibleForUser } from "@/types/site-settings";

type Item = { href: string; label: string; icon: JSX.Element };

export default function MobileNav({
  isAdmin = false,
  showGallery = false,
  canCreateGallery = false,
}: {
  isAdmin?: boolean;
  showGallery?: boolean;
  canCreateGallery?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { pages } = usePagesVisibility();

  const baseItems: Item[] = useMemo(
    () =>
      [
        { href: "/", label: "Home", icon: <Home className="h-6 w-6" /> },
        { href: "/lascuola", label: "La Scuola", icon: <School className="h-6 w-6" /> },
        { href: "/documentazione/modulistica", label: "Modulistica", icon: <FileUser className="h-6 w-6" /> },
        { href: "/documentazione/circolari", label: "Circolari", icon: <GraduationCap className="h-6 w-6" /> },
        { href: "/news", label: "News", icon: <Newspaper className="h-6 w-6" /> },
        { href: "/openday", label: "Open Day", icon: <DoorOpen className="h-6 w-6" /> },
        { href: "/contatti", label: "Contatti", icon: <MapPinHouse className="h-6 w-6" /> },
        ...(showGallery
          ? [
              {
                href: "/area-riservata/gallery",
                label: "Gallery",
                icon: <Images className="h-6 w-6" />,
              },
            ]
          : []),
        { href: "/area-riservata", label: "Area Riservata", icon: <User className="h-6 w-6" /> },
      ].filter((item) => isHrefVisibleForUser(item.href, pages)),
    [pages, showGallery],
  );

  const adminItems: Item[] = useMemo(() => {
    const createActivity: Item[] = canCreateGallery
      ? [
          {
            href: "/admin/gallery/create-activity",
            label: "Crea Attività",
            icon: <FilePlus className="h-6 w-6" />,
          },
        ]
      : [];

    if (!isAdmin) return createActivity;

    return [
      { href: "/admin/site-settings", label: "Visibilità pagine", icon: <Eye className="h-6 w-6" /> },
      { href: "/admin/circolare/create-circolare", label: "Crea Circolare", icon: <FilePlus className="h-6 w-6" /> },
      { href: "/admin/news/create-news", label: "Crea News", icon: <FilePlus className="h-6 w-6" /> },
      ...createActivity,
      { href: "/admin/newsletter/create-newsletter", label: "Invia Newsletter", icon: <FilePlus className="h-6 w-6" /> },
      { href: "/admin/users-dashboard", label: "Dashboard Utenti", icon: <ChartLine className="h-6 w-6" /> },
    ];
  }, [isAdmin, canCreateGallery]);

  return (
    <div className="w-full flex justify-between items-center p-6 lg:hidden">
      <Link href="/" className="flex items-center gap-2 cursor-pointer" aria-label="Vai alla home">
        {/* usa path pubblico per semplificare i test */}
        <Image
          src="/loghi/logo.png"
          alt="Santa Marta"
          width={150}
          height={80}
        />
      </Link>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild className="">
          <button
            aria-label="Apri menu"
            aria-expanded={open}
            aria-controls="mobile-nav"
            className="p-2 cursor-pointer"
          >
            <Hamburger
              toggled={open}
              size={28}
              rounded
              distance="sm"
              label="Apri menu"
              color="var(--card-foreground)"
            />
          </button>
        </SheetTrigger>

        <SheetContent
          side="left"
          id="mobile-nav"
          className="!inset-x-0 !left-0 !right-0 !top-[110px] !bottom-0 !h-[calc(100dvh-110px)] !max-h-[calc(100dvh-110px)] w-full max-w-none border-none bg-(--bg-secondary) text-card-foreground p-0 gap-0"
        >
          <nav
            data-testid="mobile-nav"
            className="h-full min-h-0 overflow-y-auto overscroll-contain"
          >
            <div className="flex flex-col justify-start items-start gap-6 p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              {[...baseItems, ...adminItems].map((it) => (
                <MobileNavItem key={it.href} {...it} />
              ))}
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}